import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Swords,
  Plus,
  X,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Info,
  ExternalLink,
  Target,
  Copy,
  Check,
} from 'lucide-react';
import { Card, CardTitle, CardDescription, Input, Button, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useUserPrefs } from '@/lib/userPrefs';
import { PrefilledKeyField } from '@/components/PrefilledKeyField';
import { ApiKeyGate } from '@/components/ApiKeyGate';

/**
 * Phase 41: 경쟁 블로그 격차 분석
 * 내 블로그 + 라이벌 1~3개 → AI 가 격차 + gap 키워드 + 액션 플랜
 */
export function BlogCompare() {
  const prefs = useUserPrefs();
  const [myUrl, setMyUrl] = useState(prefs.defaultBlogUrl);
  const [rivals, setRivals] = useState<string[]>(['']);
  const hasKey = !!prefs.geminiApiKey;

  const m = useMutation({
    mutationFn: () =>
      api.blogCoach.compare({
        myBlogUrl: myUrl.trim(),
        rivalBlogUrls: rivals.map((r) => r.trim()).filter(Boolean),
        geminiApiKey: prefs.geminiApiKey.trim(),
      }),
  });

  const validRivalCount = rivals.filter((r) => r.trim()).length;
  const canSubmit = !!myUrl.trim() && validRivalCount > 0 && hasKey && !m.isPending;

  function addRival() {
    if (rivals.length >= 3) return;
    setRivals([...rivals, '']);
  }
  function removeRival(i: number) {
    setRivals(rivals.filter((_, idx) => idx !== i));
  }
  function setRival(i: number, v: string) {
    setRivals(rivals.map((r, idx) => (idx === i ? v : r)));
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Swords className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">경쟁 블로그 격차 분석</span>
          <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">
            BETA
          </span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          내 블로그 vs 라이벌 비교 — AI 가 라이벌이 다루는데 내가 안 다룬 키워드를 추출합니다.
        </p>
      </header>

      <ApiKeyGate hasKey={hasKey} />

      <Card className="border-amber-400/30 bg-amber-400/5">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 shrink-0 text-amber-300 mt-0.5" />
          <p className="text-xs text-amber-100/80">
            라이벌은 같은 분야에서 더 잘 쓰는 블로그 1~3개를 골라주세요. AI 가 차이를 짚어
            "따라잡으려면 이 키워드부터" 액션 플랜을 줍니다.
          </p>
        </div>
      </Card>

      {hasKey && (
      <Card>
        <CardTitle>블로그 URL 입력</CardTitle>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) m.mutate();
          }}
        >
          <PrefilledKeyField
            label="내 블로그"
            storedValue={prefs.defaultBlogUrl}
            value={myUrl}
            onChange={setMyUrl}
            placeholder="예: blog.naver.com/myblog"
          />

          {rivals.map((r, i) => (
            <div key={i} className="flex items-end gap-2">
              <label className="block flex-1">
                <span className="text-xs text-gray-400">라이벌 #{i + 1}</span>
                <Input
                  className="mt-1"
                  placeholder="예: blog.naver.com/rival1"
                  value={r}
                  onChange={(e) => setRival(i, e.target.value)}
                  autoComplete="off"
                />
              </label>
              {rivals.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRival(i)}
                  className="h-10 inline-flex items-center justify-center rounded-lg border border-gray-800 px-2 text-gray-400 hover:text-red-300"
                  aria-label="삭제"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {rivals.length < 3 && (
            <button
              type="button"
              onClick={addRival}
              className="inline-flex items-center gap-1 text-xs text-gold hover:underline"
            >
              <Plus className="h-3 w-3" /> 라이벌 추가 (최대 3)
            </button>
          )}

          <div className="pt-2 flex items-center gap-3">
            <Button type="submit" variant="gold" disabled={!canSubmit}>
              {m.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> 비교 분석 중…
                </>
              ) : (
                <>
                  <Swords className="h-4 w-4" /> 격차 분석 시작
                </>
              )}
            </Button>
            {m.isPending && (
              <span className="text-xs text-gray-500">RSS 병렬 수집 → AI 비교 (~10-20초)</span>
            )}
          </div>
        </form>

        {m.isError && (
          <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertCircle className="inline h-4 w-4 mr-1" />
            {(m.error as Error).message}
          </p>
        )}
      </Card>
      )}

      {m.isPending && (
        <Card>
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </Card>
      )}
      {m.data && <ResultSection data={m.data} />}
    </div>
  );
}

// ─── 결과 ────────────────────────────────────────────────────

interface CompareResult {
  me: { userId: string; blogName: string | null; postCount: number };
  rivals: Array<{ userId: string; blogName: string | null; postCount: number }>;
  rivalErrors: string[];
  analysis: {
    myProfile: { summary: string; strengths: string[]; weaknesses: string[] };
    rivalProfiles: Array<{ blogId: string; summary: string; strengths: string[] }>;
    gapKeywords: Array<{ keyword: string; reason: string; rivalSource: string }>;
    actionPlan: string[];
  };
}

function ResultSection({ data }: { data: CompareResult }) {
  return (
    <div className="space-y-4">
      {data.rivalErrors.length > 0 && (
        <Card className="border-amber-400/40 bg-amber-400/5 text-xs text-amber-200">
          <strong>일부 라이벌 RSS 실패:</strong> {data.rivalErrors.join(' · ')}
        </Card>
      )}

      {/* 내 블로그 프로파일 */}
      <Card className="border-emerald-400/30 bg-emerald-400/5">
        <CardTitle className="flex items-center gap-2">
          🏠 내 블로그
          <span className="text-xs font-normal text-gray-400">
            ({data.me.blogName ?? data.me.userId} · {data.me.postCount}개 포스트)
          </span>
        </CardTitle>
        <p className="mt-2 text-sm text-emerald-100">"{data.analysis.myProfile.summary}"</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <ListBox label="강점" items={data.analysis.myProfile.strengths} tone="emerald" />
          <ListBox label="약점" items={data.analysis.myProfile.weaknesses} tone="amber" />
        </div>
      </Card>

      {/* 라이벌 프로파일들 */}
      {data.analysis.rivalProfiles.length > 0 && (
        <Card>
          <CardTitle>라이벌 블로그 정체성</CardTitle>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.analysis.rivalProfiles.map((r, i) => (
              <div
                key={i}
                className="rounded-lg border border-gray-800 bg-gray-950 p-3 text-sm"
              >
                <div className="font-bold text-white">⚔️ {r.blogId}</div>
                <p className="mt-1 text-xs text-gray-300">"{r.summary}"</p>
                {r.strengths.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {r.strengths.map((s, si) => (
                      <li key={si} className="text-[11px] text-gray-400">
                        • {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Gap Keywords */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-gold" /> 따라잡기용 Gap 키워드{' '}
            {data.analysis.gapKeywords.length}개
          </CardTitle>
          <CopyListButton items={data.analysis.gapKeywords.map((k) => k.keyword)} />
        </div>
        <CardDescription>라이벌이 다루는데 나는 안 다룬 주제 — 우선 공략 대상</CardDescription>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-2 font-medium w-10 text-right">#</th>
                <th className="py-2 pr-2 font-medium">키워드 + 이유</th>
                <th className="py-2 font-medium">라이벌 출처</th>
              </tr>
            </thead>
            <tbody>
              {data.analysis.gapKeywords.map((k, i) => (
                <tr key={i} className="border-b border-gray-900 last:border-b-0 hover:bg-gray-950/60">
                  <td className="py-2 pr-2 text-right tabular text-xs text-gray-500">{i + 1}</td>
                  <td className="py-2 pr-2 max-w-[420px]">
                    <a
                      href={`/analyze/${encodeURIComponent(k.keyword)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-white hover:text-gold"
                      title="새 탭에서 분석 — 비교 결과는 유지됩니다"
                    >
                      {k.keyword}
                      <ExternalLink className="inline ml-1 h-3 w-3 text-gray-600" />
                    </a>
                    {k.reason && <div className="mt-0.5 text-[11px] text-gray-400">{k.reason}</div>}
                  </td>
                  <td className="py-2 text-xs text-gray-500">{k.rivalSource}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 액션 플랜 */}
      <Card className="border-gold/40 bg-gold/5">
        <CardTitle className="flex items-center gap-2 text-gold">🎯 액션 플랜</CardTitle>
        <ol className="mt-3 space-y-2">
          {data.analysis.actionPlan.map((a, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-white">
              <span className="tabular text-xs font-bold text-gold mt-0.5 w-5">{i + 1}.</span>
              <span>{a}</span>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}

function ListBox({
  label,
  items,
  tone,
}: {
  label: string;
  items: string[];
  tone: 'emerald' | 'amber';
}) {
  const colors =
    tone === 'emerald'
      ? 'border-emerald-400/40 bg-emerald-400/5 text-emerald-200'
      : 'border-amber-400/40 bg-amber-400/5 text-amber-200';
  return (
    <div className={`rounded-lg border px-3 py-2 ${colors}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <ul className="mt-1 space-y-1">
        {items.map((s, i) => (
          <li key={i} className="text-xs">
            • {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CopyListButton({ items }: { items: string[] }) {
  const [copied, setCopied] = useState(false);
  async function onCopy() {
    if (items.length === 0) return;
    const text = items.join(', ');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        // ignore
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold',
        copied
          ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-200'
          : 'border-gold/40 bg-gold/10 text-gold hover:bg-gold/20',
      )}
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" /> 복사됨!
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" /> 키워드 {items.length}개 복사
        </>
      )}
    </button>
  );
}
